import { useEffect, useRef, useState } from "react";
import { getPedidoDetalleCliente } from "../../services/cliente/perfilcliente";
import { fileURL } from "../../utils/fileURL";
import { traducirEstadoPedido } from "../../utils/estadoPedido";
import "./OrdersModal.css";

const getErrMsg = (error, fallback = "Something went wrong") => {
  const raw =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message;

  if (!raw) return fallback;
  if (Array.isArray(raw)) return raw.join(", ");
  if (typeof raw === "object") return fallback;
  return String(raw);
};

function ToastStack({ toasts, onClose }) {
  if (!toasts?.length) return null;

  return (
    <div className="adc-toastStack" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`adc-toast adc-toast--${t.type || "info"}`}>
          <div className="adc-toast__body">
            <div className="adc-toast__title">{t.title}</div>
            {t.message ? <div className="adc-toast__msg">{t.message}</div> : null}
          </div>

          <button
            className="adc-toast__close"
            onClick={() => onClose(t.id)}
            aria-label="Close notification"
            type="button"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

/* Un solo formato de dinero, el mismo del carrito y del checkout.
 *
 * Antes recibia la moneda como segundo parametro y el segundo parametro
 * era el LOCALE, asi que `formatMoney(11135, "GTQ")` armaba el numero con
 * las reglas de un idioma llamado "GTQ": salia "11.135,00 $", con el punto
 * de los miles donde va la coma. En la misma pantalla, el listado de atras
 * decia "$11,135.00". El cliente veia dos numeros distintos para el mismo
 * pedido. */
export const formatMoney = (value) => {
  const n = Number(String(value ?? 0).replace(/,/g, ""));
  if (!Number.isFinite(n)) return "$0.00";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const norm = (v) => String(v ?? "").trim();

/* Un dato que no existe no ocupa lugar. La version anterior dibujaba una
   caja con un guion para cada campo vacio, asi que un pedido sin numero de
   cuenta ni fechas de envio mostraba cuatro cajas que no decian nada, del
   mismo tamaño que el total de la factura. */
function Dato({ etiqueta, valor, children }) {
  const contenido = children ?? (norm(valor) || null);
  if (!contenido) return null;

  return (
    <div className="adc-od-dato">
      <span className="adc-od-et">{etiqueta}</span>
      <div className="adc-od-valor">{contenido}</div>
    </div>
  );
}

export default function OrdersModal({ pedidoId, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef(new Map());

  const pushToast = (type, title, message, opts = {}) => {
    const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const duration = Number.isFinite(opts.duration) ? opts.duration : 4200;

    const toast = { id, type, title, message };
    setToasts((prev) => [toast, ...prev].slice(0, 3));

    const timer = window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      toastTimers.current.delete(id);
    }, duration);

    toastTimers.current.set(id, timer);
    return id;
  };

  const closeToast = (id) => {
    const timer = toastTimers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      toastTimers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  useEffect(() => {
    return () => {
      toastTimers.current.forEach((timer) => window.clearTimeout(timer));
      toastTimers.current.clear();
    };
  }, []);

  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setOrder(null);

        const res = await getPedidoDetalleCliente(pedidoId);
        if (alive) setOrder(res);
      } catch (e) {
        console.error("Order detail error:", e?.response?.data || e);
        if (alive) setOrder(null);

        pushToast(
          "error",
          "Order load failed",
          getErrMsg(e, "We couldn’t load the order details. Please try again.")
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => (alive = false);
  }, [pedidoId]);

  const items = Array.isArray(order?.items) ? order.items : [];
  const estado = traducirEstadoPedido(order?.estado_texto);
  const pagado = !!order?.pagado;

  /* El total viene de la base ya calculado. El subtotal se deduce
     restandole el core charge, para poder mostrar el desglose sin pedir
     otro dato: el cliente veia "Invoice total" a secas y no habia forma de
     saber que parte era el deposito de las piezas. */
  const total = Number(order?.total_factura || 0);
  const coreTotal = Number(order?.core_charge_total || 0);
  const subtotal = total - coreTotal;

  const pesoUnidad =
    items.find((i) => i.unidad_abreviatura || i.unidad_nombre) || {};
  const unidadPeso =
    pesoUnidad.unidad_abreviatura || pesoUnidad.unidad_nombre || "";

  const enviadoEl = formatDate(order?.fecha_enviado);
  const entregadoEl = formatDate(order?.fecha_entregado);

  const direccion = [
    norm(order?.direccion_linea_1),
    norm(order?.direccion_linea_2),
    [norm(order?.ciudad), norm(order?.estado_provincia)]
      .filter(Boolean)
      .join(", ") + (norm(order?.codigo_postal) ? ` ${order.codigo_postal}` : ""),
    norm(order?.pais),
  ].filter((l) => l && l.trim());

  return (
    <div className="adc-modal-backdrop adc-orders-scope" onClick={onClose}>
      <ToastStack toasts={toasts} onClose={closeToast} />

      <div
        className="adc-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="adc-modal-header">
          <div>
            <h2>Order #{pedidoId}</h2>

            {/* La fecha y el PO debajo del titulo y no pegados a el: el PO
                es el numero con el que el cliente busca el pedido en su
                propio sistema, no parte del nombre. */}
            <p className="adc-od-sub">
              {loading ? "Loading…" : formatDate(order?.fecha_creado) || "—"}
              {order?.no_po ? ` · PO #${order.no_po}` : ""}
            </p>
          </div>

          <button
            className="adc-modal-close"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="adc-modal-body">
          {loading && <p className="adc-od-vacio">Loading order details…</p>}

          {!loading && !order && (
            <p className="adc-od-vacio">We couldn’t load this order.</p>
          )}

          {!loading && order && (
            <>
              {/* Lo primero: en que anda el pedido y cuanto es. Era una
                  grilla de ocho cajas iguales donde el total de la factura
                  pesaba lo mismo que "Contact name". */}
              <div className="adc-od-resumen">
                <div className="adc-od-estados">
                  {estado ? (
                    <span className="adc-od-chip">{estado}</span>
                  ) : null}

                  <span
                    className={`adc-od-chip ${
                      pagado ? "adc-od-chip--pagado" : "adc-od-chip--debe"
                    }`}
                  >
                    {pagado ? "Paid" : "Not paid"}
                  </span>
                </div>

                <div className="adc-od-total">
                  <span>Invoice total</span>
                  <strong>{formatMoney(total)}</strong>
                </div>
              </div>

              {/* El recorrido del pedido en un renglon. Las fechas vacias
                  no se dibujan como cajas con guion: se dicen en palabras,
                  que es lo que el cliente quiere saber. */}
              <div className="adc-od-estado-envio">
                {entregadoEl
                  ? `Delivered on ${entregadoEl}`
                  : enviadoEl
                  ? `Shipped on ${enviadoEl}`
                  : "Not shipped yet."}
              </div>

              <div className="adc-od-cols">
                <section className="adc-od-bloque">
                  <h3>Shipping address</h3>

                  <Dato etiqueta="Recipient" valor={order.nombre_destinatario} />

                  {direccion.length > 0 ? (
                    <Dato etiqueta="Address">
                      {direccion.map((l, i) => (
                        <div key={i}>{l}</div>
                      ))}
                    </Dato>
                  ) : null}
                </section>

                <section className="adc-od-bloque">
                  <h3>Shipping</h3>

                  <Dato
                    etiqueta="Method"
                    valor={order.nombre_metodo_envio}
                  />
                  <Dato
                    etiqueta="Carrier"
                    valor={order.metodo_envio_compania}
                  />
                  <Dato
                    etiqueta="Carrier account"
                    valor={order.metodo_envio_no_cuenta}
                  />
                  <Dato etiqueta="Contact" valor={order.nombre_contacto} />
                  <Dato
                    etiqueta="Total weight"
                    valor={
                      order.peso_total != null
                        ? `${order.peso_total}${unidadPeso ? ` ${unidadPeso}` : ""}`
                        : ""
                    }
                  />
                </section>
              </div>

              <section className="adc-od-bloque">
                <h3>
                  Items
                  <span className="adc-od-cuenta">
                    {items.length} {items.length === 1 ? "product" : "products"}
                  </span>
                </h3>

                {items.length === 0 ? (
                  <p className="adc-od-vacio">No items found for this order.</p>
                ) : (
                  <>
                    <div className="adc-od-items">
                      {items.map((it) => {
                        const imgSrc = it.url_image ? fileURL(it.url_image) : null;
                        const pdfHref = it.url_documento
                          ? fileURL(it.url_documento)
                          : null;

                        return (
                          <div
                            key={it.pedido_detalles_id}
                            className="adc-od-item"
                          >
                            <div className="adc-od-img">
                              {imgSrc ? (
                                <img src={imgSrc} alt="" />
                              ) : (
                                <span>No image</span>
                              )}
                            </div>

                            <div className="adc-od-item-info">
                              <div className="adc-od-item-nombre">
                                {it.nombre_producto}
                              </div>

                              <div className="adc-od-item-meta">
                                {it.cantidad} × {formatMoney(it.precio_unitario)}
                                {it.peso_producto != null
                                  ? ` · ${it.peso_producto}${
                                      it.unidad_abreviatura || it.unidad_nombre
                                        ? ` ${
                                            it.unidad_abreviatura ||
                                            it.unidad_nombre
                                          }`
                                        : ""
                                    }`
                                  : ""}
                              </div>

                              {pdfHref && (
                                <a
                                  className="adc-od-pdf"
                                  href={pdfHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Technical documentation
                                </a>
                              )}
                            </div>

                            <div className="adc-od-item-total">
                              {formatMoney(it.subtotal)}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* El desglose. Sin esto el cliente veia los subtotales
                        de cada linea y un total mas grande, sin saber de
                        donde salia la diferencia. */}
                    <div className="adc-od-totales">
                      <div>
                        <span>Subtotal</span>
                        <span>{formatMoney(subtotal)}</span>
                      </div>

                      {coreTotal > 0 ? (
                        <div>
                          <span>Core charge</span>
                          <span>{formatMoney(coreTotal)}</span>
                        </div>
                      ) : null}

                      <div className="adc-od-totales-final">
                        <span>Total</span>
                        <span>{formatMoney(total)}</span>
                      </div>
                    </div>
                  </>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
